import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  ColumnDef,
  type Cell,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table"
import { ChevronDown, MoreHorizontal } from "lucide-react"
import { cn } from "../lib/utils"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table"
import { TableSequencePaginationProvider } from "./ui/table-sequence"
import { Button } from "./ui/button"
import { Checkbox } from "./ui/checkbox"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"
import { SearchInput } from "./search-input"
import { Skeleton } from "./ui/skeleton"
import { Pagination } from "./pagination"

export type DataTableColumnPriority = "primary" | "secondary" | "detail"
export type DataTableColumnAlignment = "start" | "center" | "end"
export type DataTableStickyEdge = "start" | "end"

export interface DataTableColumn<T> {
  /** Stable column id and, by default, the property read from each row. */
  key: string
  header: React.ReactNode
  /** Allows a concise, translated label in the mobile row drawer. */
  detailLabel?: React.ReactNode
  sortable?: boolean
  /** Backwards-compatible cell class name. Prefer the semantic options below. */
  className?: string
  cellClassName?: string
  headerClassName?: string
  render?: (row: T, index: number) => React.ReactNode
  detailRender?: (row: T, index: number) => React.ReactNode
  align?: DataTableColumnAlignment
  numeric?: boolean
  width?: number | string
  minWidth?: number | string
  responsivePriority?: DataTableColumnPriority
  sticky?: DataTableStickyEdge
}

export interface DataTableRowAction<T> {
  label: string
  icon?: React.ReactNode
  onSelect: (row: T) => void
  primary?: boolean
  destructive?: boolean
  disabled?: boolean
}

export interface DataTableSelection<T> {
  selected: Array<string | number>
  onChange: (selected: Array<string | number>) => void
  disabled?: (row: T) => boolean
  ariaLabel?: string
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  data: T[]
  loading?: boolean
  error?: React.ReactNode
  searchable?: boolean
  searchPlaceholder?: string
  /** Controlled search query. Useful for server-side lists. */
  searchValue?: string
  pageSize?: number
  /** Retained for API compatibility; page-size selection is intentionally page-owned. */
  pageSizeOptions?: number[]
  serverSide?: boolean
  totalItems?: number
  currentPage?: number
  onPageChange?: (page: number) => void
  onSearch?: (query: string) => void
  onSort?: (key: string, direction: "asc" | "desc") => void
  /** Current server-side sort state; ignored for client-side sorting. */
  sortState?: { key: string; direction: "asc" | "desc" } | null
  emptyMessage?: React.ReactNode
  keyExtractor?: (row: T) => string | number
  /** Legacy custom actions cell. Prefer rowActions for standard action menus. */
  actions?: (row: T) => React.ReactNode
  rowActions?: (row: T) => DataTableRowAction<T>[]
  selection?: DataTableSelection<T>
  onRowClick?: (row: T) => void
  className?: string
  toolbarStart?: React.ReactNode
  toolbarEnd?: React.ReactNode
  density?: "standard" | "compact"
  /** Retained for compatibility. Prefer server pagination for very large lists. */
  virtualized?: boolean
  /** Row numbers are off by default and should be reserved for audit/report views. */
  sequence?: boolean
  /** Set false for dense reports that must retain horizontal scrolling on phones. */
  mobileDetails?: boolean
  mobileDetailsTitle?: (row: T) => React.ReactNode
  renderMobileDetails?: (row: T) => React.ReactNode
}

type DataTableColumnMeta<T> = {
  column?: DataTableColumn<T>
  selection?: boolean
  actions?: boolean
}

const priorityClassName: Record<DataTableColumnPriority, string> = {
  primary: "",
  secondary: "hidden sm:table-cell",
  detail: "hidden lg:table-cell",
}

function getDefaultCellValue<T extends object>(row: T, key: string) {
  const value = (row as Record<string, unknown>)[key]
  if (value === null || value === undefined || value === "") return "—"
  return React.isValidElement(value) ? value : String(value)
}

function isInteractiveTarget(target: EventTarget | null) {
  return target instanceof HTMLElement && Boolean(
    target.closest("button, a, input, select, textarea, [role=menuitem], [data-table-row-control]"),
  )
}

export function DataTable<T extends object>({
  columns,
  data,
  loading = false,
  error,
  searchable = true,
  searchPlaceholder,
  searchValue,
  pageSize = 10,
  serverSide = false,
  totalItems,
  currentPage: serverPage = 1,
  onPageChange,
  onSearch,
  onSort,
  sortState,
  emptyMessage,
  keyExtractor,
  actions,
  rowActions,
  selection,
  onRowClick,
  className,
  toolbarStart,
  toolbarEnd,
  density = "standard",
  sequence = false,
  mobileDetails = true,
  mobileDetailsTitle,
  renderMobileDetails,
}: DataTableProps<T>) {
  const { t } = useTranslation()
  const [uncontrolledSearch, setUncontrolledSearch] = useState("")
  const [page, setPage] = useState(1)
  const [sorting, setSorting] = useState<SortingState>([])
  const [detailRow, setDetailRow] = useState<T | null>(null)
  const [isPhone, setIsPhone] = useState(false)

  const searchQuery = searchValue ?? uncontrolledSearch
  const resolvedSearchPlaceholder = searchPlaceholder ?? t("ui.searchPlaceholder")
  const resolvedEmptyMessage = emptyMessage ?? t("ui.noData")
  const selectedKeys = useMemo(() => new Set(selection?.selected ?? []), [selection?.selected])
  const hasResponsiveDetails = columns.some((column) => (column.responsivePriority ?? "primary") !== "primary")
  const canOpenMobileDetails = mobileDetails && (hasResponsiveDetails || Boolean(renderMobileDetails))

  useEffect(() => {
    if (searchValue === undefined) return
    setUncontrolledSearch(searchValue)
  }, [searchValue])

  useEffect(() => {
    const media = window.matchMedia?.("(max-width: 639px)")
    if (!media) return
    const update = () => setIsPhone(media.matches)
    update()
    media.addEventListener?.("change", update)
    return () => media.removeEventListener?.("change", update)
  }, [])

  const getRowKey = (row: T, index?: number) => keyExtractor?.(row) ?? String(index ?? 0)
  const serverSorting = useMemo<SortingState>(
    () => (sortState ? [{ id: sortState.key, desc: sortState.direction === "desc" }] : []),
    [sortState],
  )
  const effectiveSorting = serverSide ? serverSorting : sorting

  const updateSelection = (next: Array<string | number>) => selection?.onChange(next)
  const toggleRowSelection = (row: T, index: number, checked: boolean) => {
    if (!selection || selection.disabled?.(row)) return
    const key = getRowKey(row, index)
    const next = new Set(selectedKeys)
    if (checked) next.add(key)
    else next.delete(key)
    updateSelection(Array.from(next))
  }

  const columnsDef = useMemo<ColumnDef<T, unknown>[]>(() => {
    const defs: ColumnDef<T, unknown>[] = columns.map((column) => ({
      id: column.key,
      accessorKey: column.key,
      header: () => column.header,
      cell: (context) => column.render
        ? column.render(context.row.original, context.row.index)
        : getDefaultCellValue(context.row.original, column.key),
      enableSorting: Boolean(column.sortable),
      meta: { column } satisfies DataTableColumnMeta<T>,
    }))

    if (selection) {
      defs.unshift({
        id: "__selection",
        header: () => null,
        cell: () => null,
        enableSorting: false,
        meta: { selection: true } satisfies DataTableColumnMeta<T>,
      })
    }

    if (actions || rowActions) {
      defs.push({
        id: "__actions",
        header: () => t("ui.actions"),
        cell: () => null,
        enableSorting: false,
        meta: { actions: true } satisfies DataTableColumnMeta<T>,
      })
    }

    return defs
  }, [actions, columns, rowActions, selection, t])

  const table = useReactTable({
    data,
    columns: columnsDef,
    getRowId: (row, index) => String(getRowKey(row, index)),
    state: {
      sorting: effectiveSorting,
      globalFilter: serverSide ? "" : searchQuery,
      pagination: serverSide
        ? { pageIndex: Math.max(0, serverPage - 1), pageSize }
        : { pageIndex: Math.max(0, page - 1), pageSize },
    },
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(effectiveSorting) : updater
      if (serverSide) {
        const active = next[0]
        if (active) onSort?.(active.id, active.desc ? "desc" : "asc")
        return
      }
      setSorting(next)
      const active = next[0]
      if (active) onSort?.(active.id, active.desc ? "desc" : "asc")
    },
    globalFilterFn: (row, _columnId, filterValue) => {
      const query = String(filterValue ?? "").toLocaleLowerCase().trim()
      if (!query) return true
      return Object.values(row.original as Record<string, unknown>).some((value) => String(value ?? "").toLocaleLowerCase().includes(query))
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: serverSide ? undefined : getFilteredRowModel(),
    getSortedRowModel: serverSide ? undefined : getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: serverSide,
    manualSorting: serverSide,
    manualFiltering: serverSide,
    pageCount: serverSide ? Math.max(1, Math.ceil((totalItems ?? 0) / pageSize)) : undefined,
  })

  const rowModel = table.getRowModel()
  const total = totalItems ?? table.getFilteredRowModel().rows.length
  const currentPage = serverSide ? serverPage : page
  const totalPages = serverSide ? Math.max(1, Math.ceil((totalItems ?? 0) / pageSize)) : table.getPageCount()
  const selectableRows = rowModel.rows.filter((row) => !selection?.disabled?.(row.original))
  const allVisibleSelected = selectableRows.length > 0 && selectableRows.every((row) => selectedKeys.has(getRowKey(row.original, row.index)))
  const someVisibleSelected = selectableRows.some((row) => selectedKeys.has(getRowKey(row.original, row.index)))

  const handleSearch = (value: string) => {
    if (searchValue === undefined) setUncontrolledSearch(value)
    setPage(1)
    onSearch?.(value)
  }

  const handlePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages) return
    if (serverSide) onPageChange?.(nextPage)
    else setPage(nextPage)
  }

  const toggleAllVisible = (checked: boolean) => {
    if (!selection) return
    const visibleKeys = selectableRows.map((row) => getRowKey(row.original, row.index))
    const next = new Set(selectedKeys)
    visibleKeys.forEach((key) => checked ? next.add(key) : next.delete(key))
    updateSelection(Array.from(next))
  }

  const renderActionCell = (row: T) => {
    if (actions) return actions(row)
    const allActions = rowActions?.(row) ?? []
    const primary = allActions.find((action) => action.primary)
    const overflow = allActions.filter((action) => action !== primary)
    if (!primary && overflow.length === 0) return null

    return (
      <div className="flex items-center justify-end gap-1" data-table-row-control>
        {primary ? (
          <Button
            type="button"
            variant="ghost"
            size={primary.icon ? "icon-sm" : "sm"}
            className={cn(primary.destructive && "text-destructive hover:text-destructive")}
            aria-label={primary.label}
            title={primary.label}
            disabled={primary.disabled}
            onClick={() => primary.onSelect(row)}
          >
            {primary.icon ?? primary.label}
          </Button>
        ) : null}
        {overflow.length > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="icon-sm" aria-label={t("ui.actions")}>
                <MoreHorizontal className="h-4 w-4" aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {overflow.map((action) => (
                <DropdownMenuItem
                  key={action.label}
                  disabled={action.disabled}
                  className={cn(action.destructive && "text-destructive focus:text-destructive")}
                  onSelect={() => action.onSelect(row)}
                >
                  {action.icon ? <span className="me-2 [&_svg]:h-4 [&_svg]:w-4">{action.icon}</span> : null}
                  {action.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    )
  }

  const renderCell = (cell: Cell<T, unknown>, rowIndex: number) => {
    const meta = cell.column.columnDef.meta as DataTableColumnMeta<T> | undefined
    const column = meta?.column
    const priority = column?.responsivePriority ?? "primary"
    const alignClass = column?.align === "center" ? "text-center" : column?.align === "end" || column?.numeric ? "text-end" : "text-start"
    const sticky = meta?.actions ? "end" : column?.sticky
    const style = {
      width: column?.width,
      minWidth: column?.minWidth,
    }

    if (meta?.selection) {
      const row = cell.row.original
      const disabled = selection?.disabled?.(row) ?? false
      const checked = selectedKeys.has(getRowKey(row, rowIndex))
      return (
        <TableCell key={cell.id} className="w-11 px-2 text-center" data-table-sticky="start">
          <span data-table-row-control>
            <Checkbox
              checked={checked}
              disabled={disabled}
              aria-label={selection?.ariaLabel}
              onCheckedChange={(value) => toggleRowSelection(row, rowIndex, value === true)}
            />
          </span>
        </TableCell>
      )
    }

    if (meta?.actions) {
      return (
        <TableCell key={cell.id} className="w-[1%] whitespace-nowrap text-end" data-table-sticky="end">
          {renderActionCell(cell.row.original)}
        </TableCell>
      )
    }

    return (
      <TableCell
        key={cell.id}
        className={cn(priorityClassName[priority], alignClass, column?.numeric && "tabular-nums [direction:ltr]", column?.className, column?.cellClassName)}
        data-table-sticky={sticky}
        style={style}
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </TableCell>
    )
  }

  const detailFields = columns.filter((column) => column.key !== "__actions")
  const detailsTitle = detailRow ? mobileDetailsTitle?.(detailRow) ?? columns[0]?.header : null

  return (
    <div className={cn("erp-data-table space-y-0", className)} aria-busy={loading || undefined}>
      {(searchable || toolbarStart || toolbarEnd) ? (
        <div className="erp-data-table-toolbar flex min-h-14 flex-col gap-3 border border-b-0 border-border-subtle bg-card px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:px-4">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            {searchable ? (
              <SearchInput
                value={searchQuery}
                onChange={handleSearch}
                placeholder={resolvedSearchPlaceholder}
                className="w-full sm:max-w-sm"
              />
            ) : null}
            {toolbarStart}
          </div>
          {toolbarEnd ? <div className="flex shrink-0 items-center gap-2">{toolbarEnd}</div> : null}
        </div>
      ) : null}

      <TableSequencePaginationProvider page={currentPage} perPage={pageSize} total={total}>
        <div className="erp-premium-table" data-table-density={density}>
          <Table compact={density === "compact"} sequenced={sequence}>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="border-0 bg-transparent hover:bg-transparent">
                  {headerGroup.headers.map((header) => {
                    const meta = header.column.columnDef.meta as DataTableColumnMeta<T> | undefined
                    const column = meta?.column
                    const priority = column?.responsivePriority ?? "primary"
                    const sortable = header.column.getCanSort()
                    const sortDirection = header.column.getIsSorted()
                    const alignClass = column?.align === "center" ? "text-center" : column?.align === "end" || column?.numeric || meta?.actions ? "text-end" : "text-start"

                    if (meta?.selection) {
                      return (
                        <TableHead key={header.id} scope="col" className="w-11 px-2 text-center" data-table-sticky="start">
                          <Checkbox
                            checked={allVisibleSelected ? true : someVisibleSelected ? "indeterminate" : false}
                            aria-label={selection?.ariaLabel}
                            onCheckedChange={(value) => toggleAllVisible(value === true)}
                          />
                        </TableHead>
                      )
                    }

                    return (
                      <TableHead
                        key={header.id}
                        scope="col"
                        aria-sort={sortable ? (sortDirection === "asc" ? "ascending" : sortDirection === "desc" ? "descending" : "none") : undefined}
                        className={cn(priorityClassName[priority], alignClass, column?.headerClassName, meta?.actions && "w-[1%] whitespace-nowrap")}
                        data-table-sticky={meta?.actions ? "end" : column?.sticky}
                        style={{ width: column?.width, minWidth: column?.minWidth }}
                      >
                        {sortable ? (
                          <button
                            type="button"
                            className={cn("group flex h-11 w-full items-center gap-1.5 rounded-md transition-colors hover:text-foreground", alignClass === "text-end" && "justify-end", alignClass === "text-center" && "justify-center", sortDirection && "text-foreground")}
                            onClick={() => header.column.toggleSorting(sortDirection === "asc")}
                          >
                            <span className="truncate">{flexRender(header.column.columnDef.header, header.getContext())}</span>
                            <ChevronDown
                              className={cn("h-3.5 w-3.5 shrink-0 text-muted-foreground/45 transition-transform group-hover:text-foreground", sortDirection === "asc" && "rotate-180 text-primary", sortDirection === "desc" && "text-primary")}
                              aria-hidden
                            />
                          </button>
                        ) : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    )
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {loading ? Array.from({ length: Math.min(pageSize, 8) }).map((_, index) => (
                <TableRow key={`loading-${index}`}>
                  {table.getAllLeafColumns().map((column) => (
                    <TableCell key={column.id}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              )) : error ? (
                <TableRow>
                  <TableCell colSpan={table.getAllLeafColumns().length} className="h-32 text-center text-sm text-destructive" role="alert">
                    {error}
                  </TableCell>
                </TableRow>
              ) : rowModel.rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={table.getAllLeafColumns().length} className="h-32 text-center text-sm text-muted-foreground">
                    {resolvedEmptyMessage}
                  </TableCell>
                </TableRow>
              ) : rowModel.rows.map((row) => {
                const rowSelected = selectedKeys.has(getRowKey(row.original, row.index))
                const rowCanOpen = Boolean(onRowClick) || (isPhone && canOpenMobileDetails)
                const openRow = () => {
                  onRowClick?.(row.original)
                  if (isPhone && canOpenMobileDetails) setDetailRow(row.original)
                }
                return (
                  <TableRow
                    key={row.id}
                    data-state={rowSelected ? "selected" : undefined}
                    className={cn(rowCanOpen && "cursor-pointer")}
                    tabIndex={rowCanOpen ? 0 : undefined}
                    onClick={(event) => {
                      if (!isInteractiveTarget(event.target)) openRow()
                    }}
                    onKeyDown={(event) => {
                      if (!rowCanOpen || (event.key !== "Enter" && event.key !== " ")) return
                      event.preventDefault()
                      openRow()
                    }}
                  >
                    {row.getVisibleCells().map((cell) => renderCell(cell, row.index))}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      </TableSequencePaginationProvider>

      {totalPages > 1 ? (
        <div className="erp-data-table-pagination border border-t-0 border-border-subtle bg-card px-3 py-2.5 sm:px-4">
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={total} pageSize={pageSize} onPageChange={handlePageChange} />
        </div>
      ) : null}

      <Sheet open={detailRow !== null} onOpenChange={(open) => !open && setDetailRow(null)}>
        <SheetContent side="bottom" className="max-h-[80dvh] rounded-t-2xl sm:hidden">
          <SheetHeader>
            <SheetTitle>{detailsTitle}</SheetTitle>
            <SheetDescription className="sr-only">{String(detailsTitle ?? "")}</SheetDescription>
          </SheetHeader>
          {detailRow ? renderMobileDetails?.(detailRow) ?? (
            <dl className="mt-5 divide-y divide-border-subtle">
              {detailFields.map((column) => {
                const index = data.indexOf(detailRow)
                const value = column.detailRender?.(detailRow, index)
                  ?? column.render?.(detailRow, index)
                  ?? getDefaultCellValue(detailRow, column.key)
                return (
                  <div key={column.key} className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-4 py-3 text-sm">
                    <dt className="text-muted-foreground">{column.detailLabel ?? column.header}</dt>
                    <dd className={cn("min-w-0 break-words text-end text-foreground", column.numeric && "tabular-nums [direction:ltr]")}>{value}</dd>
                  </div>
                )
              })}
            </dl>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  )
}
